import { KeyboardEvent, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { useDebounce } from '../../../hooks/useDebounce';
import { usePopover } from '../../../hooks/usePopover';
import { cn } from '../../../utils/cn';
import { controlClass, describedBy, Field, FieldOwnProps } from '../../ui/Field/Field';
import { Icon } from '../../ui/Icon/Icon';
import { Portal } from '../../ui/Portal/Portal';
import { Spinner } from '../../ui/Spinner/Spinner';

/**
 * Dropdown that searches the server as you type.
 * fetcher(search) -> Promise<record[]>; getLabel/getDescription describe a record.
 * `value` is the selected id; `selectedLabel` shows it before the list has loaded (e.g. when editing).
 * onChange(id, record) — record is null when cleared.
 */
export interface SearchSelectProps<R = any> extends Omit<FieldOwnProps, 'labelAction'> {
  /** Selected record id */
  value?: string | null;
  /** Label for the selected record before the list loads (editing) */
  selectedLabel?: string;
  onChange?: (id: string | null, record: R | null) => void;
  fetcher: (search: string) => Promise<R[]>;
  getLabel?: (row: R) => string;
  getDescription?: (row: R) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  clearable?: boolean;
  footer?: ReactNode;
  className?: string;
  emptyText?: ReactNode;
  id?: string;
}

export function SearchSelect<R extends { _id: string } = any>({
  label, hint, error, required, value, selectedLabel, onChange, fetcher, getLabel = (r: any) => r.name, getDescription,
  placeholder = 'Search…', disabled, size = 'md', clearable = true, footer, containerClassName, className, emptyText = 'Nothing found', id,
}: SearchSelectProps<R>) {
  const autoId = useId();
  const btnId = id || autoId;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<R[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(selectedLabel || '');
  const q = useDebounce(query, 250);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const style = usePopover(open, anchorRef, popRef, { matchWidth: true, deps: [rows.length, loading] });
  useClickOutside([anchorRef, popRef], () => setOpen(false), open);

  useEffect(() => {
    if (selectedLabel) setPicked(selectedLabel);
  }, [selectedLabel]);
  useEffect(() => {
    if (!value) setPicked('');
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true);
    fetcher(q)
      .then((r) => alive && (setRows(r), setActive(0)))
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const choose = (r: R) => {
    setPicked(getLabel(r));
    onChange?.(r._id, r);
    setOpen(false);
    anchorRef.current?.focus();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rows[active]) choose(rows[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      anchorRef.current?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={btnId} className={containerClassName}>
      <div className="relative">
        <button
          ref={anchorRef}
          id={btnId}
          type="button"
          role="combobox"
          aria-controls={`${btnId}-list`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(btnId, error, hint)}
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            setQuery('');
            setOpen((o) => !o);
          }}
          onKeyDown={(e) => {
            if (!open && ['ArrowDown', 'Enter', ' '].includes(e.key)) {
              e.preventDefault();
              setQuery('');
              setOpen(true);
            }
          }}
          className={cn(
            controlClass({ error, disabled, open }),
            'flex items-center gap-2 pl-3 text-left',
            clearable && value ? 'pr-16' : 'pr-2.5',
            size === 'sm' ? 'h-9 text-sm' : 'h-10 text-sm',
            className,
          )}
        >
          <span className={cn('flex-1 truncate', !value && 'text-ink-300')}>{value ? picked || 'Selected' : placeholder}</span>
          <Icon name="chevronDown" size={17} className={cn('shrink-0 text-ink-400 transition-transform', open && 'rotate-180')} />
        </button>
        {clearable && value && !disabled && (
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => onChange?.(null, null)}
            className="absolute right-8 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-400 hover:bg-navy-50 hover:text-navy-800"
          >
            <Icon name="x" size={15} />
          </button>
        )}
      </div>
      {open && (
        <Portal>
          <div ref={popRef} style={{ ...style, minWidth: Math.max(Number(style.minWidth ?? 0), 280) }} className="overflow-hidden rounded-xl border border-line bg-white shadow-pop animate-pop">
            <div className="border-b border-line p-2">
              <div className="relative">
                <Icon name="search" size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Type to search"
                  aria-label="Search"
                  className="h-9 w-full rounded-lg bg-navy-50 pl-8 pr-8 text-sm outline-none focus:ring-2 focus:ring-steel/30"
                />
                {loading && <Spinner size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400" />}
              </div>
            </div>
            <ul id={`${btnId}-list`} role="listbox" className="max-h-72 overflow-y-auto p-1">
              {!loading && rows.length === 0 && <li className="px-3 py-6 text-center text-sm text-ink-400">{emptyText}</li>}
              {rows.map((r, i) => (
                <li
                  key={r._id}
                  role="option"
                  aria-selected={r._id === value}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(r)}
                  className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2', i === active && 'bg-navy-50')}
                >
                  <span className="min-w-0 flex-1">
                    <span className={cn('block truncate text-sm', r._id === value && 'font-semibold')}>{getLabel(r)}</span>
                    {getDescription && <span className="block truncate text-xs text-ink-400">{getDescription(r)}</span>}
                  </span>
                  {r._id === value && <Icon name="check" size={16} className="shrink-0 text-brand-600" />}
                </li>
              ))}
            </ul>
            {footer && <div className="border-t border-line p-1">{footer}</div>}
          </div>
        </Portal>
      )}
    </Field>
  );
}

export default SearchSelect;
